import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./otpPage.css";

const OtpPage = ({ onClose, onVerificationSuccess }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const hiddenInputRef = useRef(null);

  // Effect to handle autofill from SMS
  useEffect(() => {
    const handleAutofill = (e) => {
      const value = e.target.value.replace(/\D/g, "");
      if (value.length === 4) {
        const newOtp = value.split("");
        setOtp(newOtp);

        // Update all input fields
        newOtp.forEach((digit, index) => {
          if (inputRefs.current[index]) {
            inputRefs.current[index].value = digit;
          }
        });

        // Focus the last input after autofill
        if (inputRefs.current[3]) {
          inputRefs.current[3].focus();
        }
      }
    };

    const hiddenInput = hiddenInputRef.current;
    if (hiddenInput) {
      hiddenInput.addEventListener("input", handleAutofill);
    }

    return () => {
      if (hiddenInput) {
        hiddenInput.removeEventListener("input", handleAutofill);
      }
    };
  }, []);

  // Add this useEffect to handle direct changes to the hidden input
  useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = otp.join("");
    }
  }, [otp]);

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    if (value.length > 1) {
      // Handle pasting into a single field
      if (value.length === 4) {
        const newOtp = value.split("").slice(0, 4);
        setOtp(newOtp);

        // Update all input fields
        newOtp.forEach((digit, i) => {
          if (inputRefs.current[i]) {
            inputRefs.current[i].value = digit;
          }
        });

        // Focus the last input
        if (inputRefs.current[3]) {
          inputRefs.current[3].focus();
        }
        return;
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setErrorMessage("");

    // Auto-focus next input
    if (value.length === 1 && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // Move to previous field if current is empty
        inputRefs.current[index - 1].focus();
      } else {
        // Clear current field
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text/plain").replace(/\D/g, "");
    if (pasteData.length === 4) {
      const newOtp = pasteData.split("").slice(0, 4);
      setOtp(newOtp);

      // Update all input fields
      newOtp.forEach((digit, index) => {
        if (inputRefs.current[index]) {
          inputRefs.current[index].value = digit;
        }
      });

      inputRefs.current[3].focus();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 4) {
      setErrorMessage("برجاء ادخال رمز التحقق المكون من 4 أرقام");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const storedId = localStorage.getItem("verificationId");
      const response = await axios.post(
        `https://api.generalhome.sa/api/SMS/VerifyOTP?code=${enteredOtp}&id=${storedId}`,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
        }
      );

      if (response.data?.code === "1") {
        onVerificationSuccess();
      } else {
        setErrorMessage("رمز التحقق غير صالح. يرجى المحاولة مرة أخرى.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "حدث خطأ في الاتصال بالشبكة. يرجى المحاولة مرة أخرى لاحقًا.";
      setErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="otp-popup-overlay">
      <section className="otpPage">
        <h2>رمز التحقق</h2>
        <div className="otpPage-container container">
          <p className="intro">الرجاء ادخال رمز التحقق المرسل لجوالك</p>

          <form onSubmit={submit}>
            {/* Hidden input for SMS autofill */}
            <input
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d*"
              maxLength="4"
              className="hidden-otp-input"
            />

            <div className="otp-input-container" dir="ltr">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  ref={(el) => (inputRefs.current[index] = el)}
                  tabIndex={index + 1}
                  className="otp-input"
                  autoFocus={index === 0}
                  inputMode="numeric"
                  pattern="\d*"
                />
              ))}
            </div>

            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <div className="button-box">
              <button
                type="button"
                className="cancel-button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                إلغاء
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "جاري التحقق..." : "إرسال"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default OtpPage;
